import type { Dispatch, FC, SetStateAction } from 'react';
import React, { memo, useRef } from 'react';
import type { Row, TableBodyPropGetter, TableBodyProps } from 'react-table';

import type { EntityModel } from '../../../pages/Trafficking/TraffickingPage/modelConverters';
import shallowEqual from '../../../utils/shallowEqual';
import { TraffickingTableRows } from '../TraffickingTableRows';

const TraffickingTableBody: FC<{
  getTableBodyProps: (propGetter?: TableBodyPropGetter<EntityModel> | undefined) => TableBodyProps;
  loading: boolean;
  errorMessage?: string;
  prepareRow: (row: Row<EntityModel>) => void;
  rows: Row<EntityModel>[];
  selectedColIndex: number;
  selectedRowIndex: number;
  isColumnResizing: boolean;
  lastSelectedRowId: string;
  setLastSelectedRowId: Dispatch<SetStateAction<string>>;
  /** Visible column ids in header order; must change when columns reorder so body memo is not stale vs thead. */
  visibleColumnIdsKey: string;
  /** Stringified selected row ids; changes when any row is selected/deselected so the body memo lets the render through. */
  selectedRowIdsKey: string;
}> = ({
  getTableBodyProps,
  loading,
  errorMessage,
  selectedColIndex,
  selectedRowIndex,
  prepareRow,
  rows,
  lastSelectedRowId,
  setLastSelectedRowId,
  visibleColumnIdsKey,
  selectedRowIdsKey: _selectedRowIdsKey,
}) => {
  const tbodyRef = useRef<HTMLTableSectionElement>(null);

  return (
    <tbody ref={tbodyRef} {...getTableBodyProps()}>
      {!loading && !errorMessage && (
        <TraffickingTableRows
          tbodyRef={tbodyRef}
          prepareRow={prepareRow}
          rows={rows}
          selectedColIndex={selectedColIndex}
          selectedRowIndex={selectedRowIndex}
          lastSelectedRowId={lastSelectedRowId}
          setLastSelectedRowId={setLastSelectedRowId}
          visibleColumnIdsKey={visibleColumnIdsKey}
        />
      )}
    </tbody>
  );
};

/**
 * react-table's useTable returns new getTableBodyProps / prepareRow function references every render.
 * Shallow-comparing them defeats memo and re-renders the whole body (and TraffickingTableRows) on
 * unrelated parent updates. Compare everything else; tbody still calls the latest fns when we do render.
 */
const areEqual = (
  prevProps: React.ComponentProps<typeof TraffickingTableBody>,
  nextProps: React.ComponentProps<typeof TraffickingTableBody>
): boolean => {
  if (nextProps.isColumnResizing) {
    return true;
  }

  const { getTableBodyProps: _a, prepareRow: _b, ...prevRest } = prevProps;
  const { getTableBodyProps: _c, prepareRow: _d, ...nextRest } = nextProps;

  return shallowEqual(prevRest, nextRest);
};

export default memo(TraffickingTableBody, areEqual);
